import { EButtonIntents } from '@src/typings';
import { Badge, Divider } from 'antd';
import { useEffect, useState } from 'react';
import { TbArrowDown, TbArrowsSort, TbArrowUp } from 'react-icons/tb';
import { Button } from '../Button';
import { Modal } from '../Modal';
import { withTheme } from '../ThemeProvider/withTheme';
import { ISort, ISortItem } from './types.d';

function SortComponent({
    label,
    sortList = [],
    asButton = true,
    display = 'both', // "label" | "icon" | "both"
    Component,
    buttonsList = [{ key: 'confirm', label: 'Confirm' }],
    onConfirm,
    onCancel,
    filteredCount,
    prevFilters,
    defaultSort,
    theme,
}: ISort & {
    asButton?: boolean;
    display?: 'label' | 'icon' | 'both';
    defaultSort?: Record<string, 'asc' | 'desc'>;
}) {
    const [open, setIsOpen] = useState(false);
    const [selectedSort, setSelectedSort] = useState<
        Record<string, 'asc' | 'desc' | null>
    >({});

    useEffect(() => {
        if (defaultSort) {
            setSelectedSort(defaultSort);
        } else if (prevFilters) {
            const firstSelected = Object.entries(prevFilters).find(
                ([, dir]) => !!dir
            );
            if (firstSelected) {
                setSelectedSort({
                    [firstSelected[0]]:
                        firstSelected[1] === true ? 'asc' : 'desc',
                });
            }
        }
    }, [defaultSort, prevFilters]);

    const toggleSort = (id: string) => {
        setSelectedSort((prev) => {
            const current = prev[id] ?? null;
            const next =
                current === null ? 'asc' : current === 'asc' ? 'desc' : null;
            return { [id]: next };
        });
    };

    const handleCancel = () => {
        if (defaultSort) {
            setSelectedSort(defaultSort);
        } else if (prevFilters) {
            const firstSelected = Object.entries(prevFilters).find(
                ([, dir]) => !!dir
            );
            if (firstSelected) {
                setSelectedSort({
                    [firstSelected[0]]:
                        firstSelected[1] === true ? 'asc' : 'desc',
                });
            } else {
                setSelectedSort({});
            }
        } else {
            setSelectedSort({});
        }

        onCancel?.();
        setIsOpen(false);
    };

    const handleConfirm = () => {
        const sorted = Object.entries(selectedSort).reduce(
            (acc, [id, dir]) => {
                if (dir === 'asc' || dir === 'desc') {
                    acc[id] = dir;
                }
                return acc;
            },
            {} as Record<string, 'asc' | 'desc'>
        );

        onConfirm(sorted);
        setIsOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(true);
        }
    };

    const renderTrigger = () => {
        const content = (
            <div className="flex items-center space-x-1">
                {(display === 'icon' || display === 'both') && (
                    <TbArrowsSort fill={theme?.colors.neutral.light.text} />
                )}
                {(display === 'label' || display === 'both') && (
                    <span>{label}</span>
                )}
            </div>
        );

        if (asButton) {
            return (
                <Badge
                    count={filteredCount}
                    color={theme?.colors.primary.main.filled}
                    offset={[-18, -2]}
                >
                    <Button
                        intent={EButtonIntents.LightFilled}
                        onClick={() => setIsOpen(true)}
                    >
                        {content}
                    </Button>
                </Badge>
            );
        }

        return (
            <div
                role="button"
                tabIndex={0}
                className="inline-flex items-center cursor-pointer"
                onClick={() => setIsOpen(true)}
                onKeyDown={handleKeyDown}
            >
                <Badge
                    count={filteredCount}
                    color={theme?.colors.primary.main.filled}
                    offset={[-10, 0]}
                >
                    {content}
                </Badge>
            </div>
        );
    };

    return (
        <div className="relative">
            {renderTrigger()}

            <Modal
                open={open}
                onCancel={handleCancel}
                title={label}
                icon={<TbArrowsSort className="text-primary" size={24} />}
                buttonsList={buttonsList.map((button) => (
                    <Button
                        key={button.key}
                        intent={EButtonIntents.Filled}
                        onClick={handleConfirm}
                    >
                        {button.label}
                    </Button>
                ))}
            >
                {sortList.length > 0 && (
                    <div className="flex flex-col space-y-2 mb-3">
                        {sortList.map((item: ISortItem) => (
                            <button
                                key={item.id}
                                className="flex items-center py-1 cursor-pointer text-left"
                                onClick={() => toggleSort(item.id)}
                            >
                                <span>
                                    {selectedSort[item.id] === 'asc' && (
                                        <TbArrowUp className="text-primary" />
                                    )}
                                    {selectedSort[item.id] === 'desc' && (
                                        <TbArrowDown className="text-primary" />
                                    )}
                                </span>
                                <span>{item.label}</span>
                            </button>
                        ))}
                    </div>
                )}

                {Component && (
                    <>
                        {sortList.length > 0 && <Divider />}
                        {Component}
                    </>
                )}
            </Modal>
        </div>
    );
}

export const Sort = withTheme(SortComponent);
